import { Handle, type HandleType, type Position } from '@xyflow/react';
import React from 'react';

import { createHandleId } from '../../utils/handle-ids';

export interface NodeHandleProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'id'> {
  type: HandleType;
  position: Position;
  id: string;
  blockId?: string; // Block ID for prefixing
  index?: number;
  total?: number;
  offset?: number;
  // Visual state for connection indicators
  isValidTarget?: boolean;
  isActiveConnection?: boolean;
  isConfigPort?: boolean;
}

/**
 * Calculate vertical position for handle based on index and total count
 */
const calculateHandlePosition = (
  index: number = 0,
  total: number = 1,
  offset: number = 25,
): number => {
  if (total === 1) {
    return 50; // Center for single handle
  }

  // Distribute handles evenly with offset from top and bottom
  const spacing = (100 - offset * 2) / Math.max(1, total - 1);
  return offset + index * spacing;
};

export const NodeHandle: React.FC<NodeHandleProps> = ({
  type,
  position,
  id,
  blockId,
  index = 0,
  total = 1,
  offset = 25,
  style,
  isValidTarget,
  isActiveConnection,
  isConfigPort,
  className,
  ...rest
}) => {
  const topPosition = calculateHandlePosition(index, total, offset);

  // Create prefixed handle ID if blockId is provided
  const handleId = blockId ? createHandleId(blockId, id) : id;

  // Determine CSS classes based on connection state
  const getHandleClassName = () => {
    const classes = [];

    if (className) {
      classes.push(className);
    }

    // Add config port styling
    if (isConfigPort) {
      classes.push('handle-config-port');
    }

    // Only apply visual indicators for target handles during active connections
    if (type === 'target' && isActiveConnection) {
      if (isValidTarget) {
        classes.push('handle-valid-target');
      } else {
        classes.push('handle-invalid-target');
      }
    }

    return classes.length > 0 ? classes.join(' ') : undefined;
  };

  return (
    <Handle
      type={type}
      position={position}
      id={handleId}
      className={getHandleClassName()}
      style={{
        top: `${topPosition}%`,
        transform: 'translateY(-50%)', // Center the handle vertically
        ...style, // Allow custom styles to override calculated positioning
      }}
      {...rest}
    />
  );
};

export interface MultiHandleProps {
  type: HandleType;
  position: Position;
  ports: string[];
  blockId?: string; // Block ID for prefixing
  offset?: number;
  // Visual state for connection indicators
  isActiveConnection?: boolean;
  getIsValidTarget?: (port: string) => boolean;
  getIsConfigPort?: (port: string) => boolean;
}

/**
 * Render multiple handles for a node side
 */
export const MultiHandle: React.FC<MultiHandleProps> = ({
  type,
  position,
  ports,
  blockId,
  offset = 25,
  isActiveConnection,
  getIsValidTarget,
  getIsConfigPort,
}) => {
  if (ports.length === 0) return null;

  return (
    <>
      {ports.map((port, index) => (
        <NodeHandle
          key={port}
          type={type}
          position={position}
          id={port}
          blockId={blockId}
          index={index}
          total={ports.length}
          offset={offset}
          isActiveConnection={isActiveConnection}
          isValidTarget={getIsValidTarget ? getIsValidTarget(port) : undefined}
          isConfigPort={getIsConfigPort ? getIsConfigPort(port) : undefined}
        />
      ))}
    </>
  );
};
